import { LocalStorageService } from 'angular-2-local-storage';
import { MonacoEditorService } from './../../../services/monaco-editor.service';
import { first } from 'rxjs/operators';
import { Component, EventEmitter, ElementRef, Output, forwardRef, HostBinding, OnInit, ViewChild, HostListener, Input, SimpleChanges, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { editor } from 'monaco-editor';
declare let monaco: any;

export interface LineWithMessage {
	lineNumber: number;
	message?: string;
}

@Component({
	selector: 'bb-code-editor',
	templateUrl: './bb-code-editor.component.html',
	styleUrls: ['./bb-code-editor.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbCodeEditorComponent),
		multi: true
	}]
})

export class BbCodeEditorComponent implements ControlValueAccessor, OnInit, OnChanges {
	// https://stackoverflow.com/questions/71072724/implement-monaco-editor-in-angular-13
	@Output() onChanged = new EventEmitter<any>();
	@Input() inModal = false;
	@Input() editorConfig: editor.IStandaloneEditorConstructionOptions = {};
	@Input() hideFullscreen = false;
	@Input() theme: 'light-v2';
	@Input() useMacroSuggestions = false;
	@Input() highlightLineNumber: number;
	@Input() errorLineNumbers: LineWithMessage[];

	@Output() modalClose: EventEmitter<any> = new EventEmitter<any>();
	@Output() change: EventEmitter<any> = new EventEmitter<any>();

	el: HTMLElement;
	modelValue: string;
	currentHighlightedLineId: string[] = [];
	currentErrorLineId: string[] = [];

	editorOptions: editor.IStandaloneEditorConstructionOptions = {
		theme: 'vs-light',
		language: 'javascript',
		roundedSelection: true,
		autoIndent: 'full',
		automaticLayout: true,
		renderLineHighlight: 'gutter',
		minimap: {
			enabled: false
		},
		glyphMargin: true
	};
	fullscreenEditorOptions: editor.IStandaloneEditorConstructionOptions = {
		lineNumbers: 'on'
	};
	_editor: editor.ICodeEditor;
	@ViewChild('editorContainer', { static: true }) _editorContainer: ElementRef;
	@HostBinding('class.dark-mode') get isDarkMode() {
		return (this.localStorageService.get('ovp-editor-theme') || this.editorOptions.theme) === 'vs-dark';
	}
	@HostBinding('class.in-modal') get isInModal() {
		return this.inModal;
	}

	@HostListener('document:keydown', ['$event'])
	onKeydownHandler(event: KeyboardEvent) {
		if (event.key === 'Escape' && this.isFullscreen) {
			event.stopPropagation();
			this.closeModal();
			this.toggleFullScreen();
		}
	}

	constructor(
		private _eref: ElementRef,
		private monacoEditorService: MonacoEditorService,
		private localStorageService: LocalStorageService
	) {
		this.el = this._eref.nativeElement;
	}

	private initMonaco(): void {
		this.monacoEditorService.load();
		if (!this.monacoEditorService.loaded) {
			this.monacoEditorService.loadingFinished.pipe(first()).subscribe(() => {
				this.initMonaco();
			});
			return;
		}

		const storedTheme: string = this.localStorageService.get('ovp-editor-theme');
		if (storedTheme) {
			this.editorOptions.theme = storedTheme;
		}

		// custom theme for srt
		if (this.editorConfig.language === 'srt') {
			this.editorOptions.theme = this.editorOptions.theme === 'vs-light' ? 'srt' : 'srt-dark';
		}

		this._editor = monaco.editor?.create(
			this._editorContainer.nativeElement,
			{ ...this.editorOptions, ...this.editorConfig },
		);

		if (this.modelValue) {
			this._editor.setValue(this.modelValue);
		}

		this._editor.onDidChangeModelContent(() => {
			this.checkChanges();
		});

		this._editor.onDidBlurEditorWidget(() => {
			this.checkChanges();
		});
	}

	closeModal() {
		this.inModal = false;
		this.modalClose.emit();
	}

	@HostListener('click', ['$event'])
	onClick(event: MouseEvent): void {
		if ((event?.target as HTMLElement)?.classList.contains('in-modal')) {
			event.preventDefault();
			this.closeModal();
		}
	}

	ngOnInit() {
		this.initMonaco();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.editorConfig?.currentValue) {
			this.updateEditorOptions(changes.editorConfig.currentValue);
		}
		if (changes.highlightLineNumber) {
			this.setHighlightLineNumbers(changes.highlightLineNumber?.currentValue);
		}
		if (changes.errorLineNumbers) {
			this.setHighlightLineNumbers(changes.errorLineNumbers?.currentValue, true);
		}
	}

	public setHighlightLineNumbers(lineNumber: number | LineWithMessage[], error = false): void {
		if (!this._editor?.deltaDecorations) {
			return;
		}

		// clear current
		if (error) {
			this.currentErrorLineId = this._editor.deltaDecorations(this.currentErrorLineId, []);
		} else {
			this.currentHighlightedLineId = this._editor.deltaDecorations(this.currentHighlightedLineId, []);
		}

		if (!lineNumber) {
			return;
		}

		if (typeof lineNumber === 'number') {
			lineNumber = [{ lineNumber }];
		}

		const isDarkMode = this.editorOptions.theme.includes('dark');
		const newLines = lineNumber.map((line: LineWithMessage) => {
			// default text color dark mode is unreadable so set it
			const dispMessage = !line.message ? null : {
				value: isDarkMode ? `<span style="color:#f3f3f3;">${line.message}</span>` : line.message,
				isTrusted: true,
				supportHtml: true
			};

			return {
				range: new monaco.Range(line.lineNumber, 1, line.lineNumber, 1),
				options: {
					isWholeLine: true,
					className: error ? '' : 'code-line-highlight',
					hoverMessage: (!error && dispMessage) ? dispMessage : null,
					// display error indicator in left bar with optional hover message
					glyphMarginClassName: error ? 'code-error-highlight' : '',
					glyphMarginHoverMessage: (error && dispMessage) ? dispMessage : null,
					// display in scrollbar on the right
					overviewRuler: { position: editor.OverviewRulerLane.Full, color: error ? '#e54f2d' : '#88dbfe' }
				}
			};
		});

		// draw new lines
		if (error) {
			this.currentErrorLineId = this._editor.deltaDecorations([], newLines);
		} else {
			this.currentHighlightedLineId = this._editor.deltaDecorations([], newLines);
		}
	}

	updateEditorOptions(config) {
		if (!config || !this._editor) {
			return;
		}
		let newOptions = { ...this.editorOptions, ...config };
		if (this.isFullscreen) {
			newOptions = { ...newOptions, ...this.fullscreenEditorOptions };
		}
		this._editor.updateOptions(newOptions);
	}

	toggleTheme() {
		this.editorOptions.theme = this.editorOptions.theme.includes('dark') ? 'vs-light' : 'vs-dark';
		this.localStorageService.set('ovp-editor-theme', this.editorOptions.theme);

		// custom theme for srt
		if (this.editorConfig.language === 'srt') {
			this.editorOptions.theme = this.editorOptions.theme === 'vs-light' ? 'srt' : 'srt-dark';
		}
		this._editor.updateOptions(this.editorOptions);

		// set correct hover color
		this.setHighlightLineNumbers(this.highlightLineNumber);
		this.setHighlightLineNumbers(this.errorLineNumbers, true);
	}

	toggleFullScreen() {
		this.el.classList.toggle('fullscreen');
	}

	get isFullscreen() {
		return this.inModal || this.el.classList.contains('fullscreen');
	}

	checkChanges() {
		const editorValue = this._editor.getValue();
		if (this.modelValue !== editorValue) {
			this.modelValue = editorValue;
			this.propagateChange(this.modelValue);
			this.change.emit(this.modelValue);
		}
	}

	onSuggestionSelected(suggestOption) {
		if (!suggestOption?.value || !this._editor) {
			return;
		}
		const pos = this._editor.getPosition();
		const range = new monaco.Range(pos.lineNumber, pos.column, pos.lineNumber, pos.column);
		const id = { major: 1, minor: 1 };
		const op = { identifier: id, range, text: suggestOption?.value, forceMoveMarkers: true };
		this._editor.executeEdits('my-source', [op]);
		this.checkChanges();
	}

	// ControlValueAccessor interface methods
	writeValue(value: any) {
		if (value !== this.modelValue) {
			this.modelValue = value || '';
			this._editor?.setValue(value || '');
		}
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}
}
